import { pickT } from "@repo/shared";
import type { FlowField, Translated } from "@repo/shared/types";
import { Copy, GripVertical, Settings, Trash2 } from "lucide-react";
import React, { useState } from "react";
import {
  getFlowComponentLabel,
  getFlowComponentRowLabel,
} from "@/components/flow/flow-component-types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import type { FlowPageComponent } from "@/lib/types/flow";
import { getComponentIcon } from "../../../../shared/component-icons";
import { T } from "./layout-block.i18n";

// Move ComponentItem OUTSIDE to prevent recreation on every render
const ComponentItem = React.memo(
  ({
    component,
    column,
    index,
    draggedComponentRef,
    draggedItem,
    disabled,
    flowLanguage,
    onDragStart,
    onDragEnd,
    onDrop,
    onComponentSettings,
    onComponentDelete,
    onComponentDuplicate,
    getComponentLabel,
    hasConditionalDisplay,
    translations,
  }: {
    component: FlowPageComponent & { flowField?: FlowField };
    column: "left" | "right";
    index: number;
    draggedComponentRef: React.RefObject<string | null>;
    draggedItem: any;
    disabled?: boolean;
    flowLanguage: string;
    onDragStart: (
      e: React.DragEvent,
      componentId: string,
      column: "left" | "right",
      index: number,
    ) => void;
    onDragEnd: () => void;
    onDrop: (e: React.DragEvent, targetColumn: "left" | "right", targetIndex: number) => void;
    onComponentSettings: (component: FlowPageComponent & { flowField?: FlowField }) => void;
    onComponentDelete: (component: FlowPageComponent & { flowField?: FlowField }) => void;
    onComponentDuplicate: (component: FlowPageComponent & { flowField?: FlowField }) => void;
    getComponentLabel: (component: FlowPageComponent & { flowField?: FlowField }) => string;
    hasConditionalDisplay: (component: FlowPageComponent) => boolean;
    translations: Translated<typeof T>;
  }) => {
    const [isDropTarget, setIsDropTarget] = useState(false);
    const dragHandleRef = React.useRef<HTMLDivElement>(null);

    // Check if this component is being dragged using the ref
    const isDragging = draggedComponentRef.current === component.id;

    return (
      // biome-ignore lint/a11y/noStaticElementInteractions: drag-and-drop requires interactive div
      <div
        key={component.id}
        className={`
        group relative
        flex items-center gap-3 p-3 border rounded-lg
        bg-background hover:bg-accent/50
        transition-all
        ${isDragging ? "opacity-30" : ""}
        ${isDropTarget && !isDragging ? "border-primary bg-primary/5 border-2" : ""}
      `}
        onDragEnter={(e) => {
          e.preventDefault();
          // Don't allow layout blocks to be dropped into 2-column layouts
          const isLayoutBlock =
            draggedItem?.type === "page-item" && draggedItem?.itemType === "layout";
          if (draggedItem && !isDragging && !isLayoutBlock) {
            setIsDropTarget(true);
          }
        }}
        onDragOver={(e) => {
          // Don't allow layout blocks to be dropped here
          const isLayoutBlock =
            draggedItem?.type === "page-item" && draggedItem?.itemType === "layout";
          if (isLayoutBlock) {
            e.dataTransfer.dropEffect = "none";
            return;
          }
          e.preventDefault();
          e.stopPropagation();
          e.dataTransfer.dropEffect = "move";
        }}
        onDragLeave={(e) => {
          // Only clear if we're leaving the entire component
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setIsDropTarget(false);
          }
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDropTarget(false);
          // Don't allow layout blocks to be dropped into 2-column layouts
          const isLayoutBlock =
            draggedItem?.type === "page-item" && draggedItem?.itemType === "layout";
          if (isLayoutBlock) {
            return;
          }
          onDrop(e, column, index);
        }}
      >
        {/* Separate drag handle that's always draggable */}
        {/* biome-ignore lint/a11y/noStaticElementInteractions: drag handle requires drag events */}
        <div
          ref={dragHandleRef}
          draggable={true} // ALWAYS TRUE - don't depend on disabled
          onDragStart={(e) => {
            if (disabled) {
              e.preventDefault();
              return;
            }
            e.stopPropagation();
            onDragStart(e, component.id, column, index);
          }}
          onDragEnd={(e) => {
            e.stopPropagation();
            onDragEnd();
          }}
          className="cursor-move p-1 -m-1 hover:bg-accent rounded"
        >
          <GripVertical className="h-5 w-5 text-muted-foreground" />
        </div>
        {(() => {
          const ComponentIcon = getComponentIcon(component.componentType);
          return (
            <ComponentIcon className="h-5 w-5 text-muted-foreground flex-shrink-0 pointer-events-none" />
          );
        })()}

        <div className="flex-1 min-w-0 pointer-events-none">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs flex-shrink-0">
              {getFlowComponentLabel(component.componentType, flowLanguage)}
            </Badge>
            <span className="font-medium truncate">{getComponentLabel(component)}</span>
            {component.componentType === "field" && component.flowField?.isRequired && (
              <Badge variant="destructive" className="text-xs">
                {translations.required}
              </Badge>
            )}
            {hasConditionalDisplay(component) && (
              <Badge variant="secondary" className="text-xs">
                {translations.conditional}
              </Badge>
            )}
          </div>
        </div>

        <div
          className={`flex gap-1 flex-shrink-0 ${draggedItem ? "pointer-events-none" : "pointer-events-auto"}`}
        >
          <Button
            variant="ghost"
            size="icon"
            tooltip={translations.settings}
            onClick={(e) => {
              e.stopPropagation();
              onComponentSettings(component);
            }}
            disabled={!!draggedItem}
          >
            <Settings className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            tooltip={translations.duplicateComponent}
            onClick={(e) => {
              e.stopPropagation();
              onComponentDuplicate(component);
            }}
            disabled={!!draggedItem}
          >
            <Copy className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            tooltip={translations.delete}
            onClick={(e) => {
              e.stopPropagation();
              onComponentDelete(component);
            }}
            disabled={!!draggedItem}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  },
);

ComponentItem.displayName = "ComponentItem";

type LayoutBlockProps = {
  layoutId: string;
  layoutConfig?: string | null; // JSON configuration including conditional display
  leftComponents: (FlowPageComponent & { flowField?: FlowField })[];
  rightComponents: (FlowPageComponent & { flowField?: FlowField })[];
  flowLanguage: string;
  onDelete: () => void;
  onLayoutSettings: () => void; // New settings callback
  onColumnSettings: (column: "left" | "right") => void; // Column settings callback
  onAddToLeft: () => void;
  onAddToRight: () => void;
  onComponentSettings: (component: FlowPageComponent & { flowField?: FlowField }) => void;
  onComponentDelete: (component: FlowPageComponent & { flowField?: FlowField }) => void;
  onComponentDuplicate: (component: FlowPageComponent & { flowField?: FlowField }) => void;
  onReorderColumn: (
    column: "left" | "right",
    componentOrders: { id: string; order: number }[],
  ) => void;
  onMoveComponentBetweenColumns: (
    componentId: string,
    toColumn: "left" | "right",
    toOrder: number,
  ) => void;
  onDragStart: (componentId: string, column: "left" | "right") => void;
  onDragEnd: () => void;
  onLayoutDragStart?: () => void;
  onLayoutDrop?: () => void;
  draggedItem: {
    id: string;
    type: "page-item" | "layout-component" | "tabs-component";
    sourceLayoutId?: string;
    sourceColumn?: "left" | "right";
    sourceTabId?: string;
    itemType?: "layout" | "component";
  } | null;
  disabled?: boolean;
};

export function LayoutBlock({
  layoutId,
  layoutConfig,
  leftComponents,
  rightComponents,
  flowLanguage,
  onDelete,
  onLayoutSettings,
  onColumnSettings,
  onAddToLeft,
  onAddToRight,
  onComponentSettings,
  onComponentDelete,
  onComponentDuplicate,
  onReorderColumn,
  onMoveComponentBetweenColumns,
  onDragStart,
  onDragEnd,
  onLayoutDragStart,
  onLayoutDrop,
  draggedItem,
  disabled,
}: LayoutBlockProps) {
  const t = pickT(T, flowLanguage);

  // Local state for drag feedback and tracking
  const [localDragState, setLocalDragState] = useState<{
    componentId: string;
    column: "left" | "right";
    index: number;
  } | null>(null);

  // Use a ref to track which component is being dragged to avoid re-renders
  const draggedComponentRef = React.useRef<string | null>(null);

  // Check if layout has conditional display
  const hasLayoutConditionalDisplay = () => {
    try {
      const config = layoutConfig ? JSON.parse(layoutConfig) : null;
      return config?.conditionalDisplay?.enabled === true;
    } catch {
      return false;
    }
  };

  // Check if a column has settings configured
  const hasColumnSettings = (column: "left" | "right") => {
    try {
      const config = layoutConfig ? JSON.parse(layoutConfig) : null;
      const columnConfig = column === "left" ? config?.leftColumn : config?.rightColumn;
      return (
        (columnConfig?.style && columnConfig.style !== "none") ||
        columnConfig?.title ||
        columnConfig?.description
      );
    } catch {
      return false;
    }
  };

  const getComponentLabel = (component: FlowPageComponent & { flowField?: FlowField }) =>
    getFlowComponentRowLabel(component, flowLanguage);

  const hasConditionalDisplay = (component: FlowPageComponent) => {
    try {
      const config = component.config ? JSON.parse(component.config) : null;
      return config?.conditionalDisplay?.enabled === true;
    } catch {
      return false;
    }
  };

  // Drag handlers
  const handleDragStart = (
    e: React.DragEvent,
    componentId: string,
    column: "left" | "right",
    index: number,
  ) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", componentId);

    // Get the actual width of the column container
    const columnElement = (e.target as HTMLElement).closest(".space-y-2") as HTMLElement;
    const elementWidth = columnElement ? columnElement.offsetWidth : 400;

    // Create custom drag preview - half-width box matching actual column width
    const dragPreview = document.createElement("div");
    dragPreview.style.cssText = `
      position: fixed;
      top: -9999px;
      left: -9999px;
      width: ${elementWidth}px;
      height: 60px;
      background: rgba(59, 130, 246, 0.2);
      border: 2px dashed rgb(59, 130, 246);
      border-radius: 8px;
      padding: 16px;
      font-size: 14px;
      font-weight: 500;
      color: rgb(59, 130, 246);
      display: flex;
      align-items: center;
      justify-content: center;
    `;
    dragPreview.textContent = t.movingComponent;
    document.body.appendChild(dragPreview);
    e.dataTransfer.setDragImage(dragPreview, 20, 30);

    // Clean up after next frame
    requestAnimationFrame(() => {
      document.body.removeChild(dragPreview);
    });

    // Store in ref to avoid re-renders
    draggedComponentRef.current = componentId;
    setLocalDragState({ componentId, column, index });

    // Delay parent state update to allow browser to lock in the drag operation
    setTimeout(() => {
      onDragStart(componentId, column);
    }, 0);
  };

  const handleDragEnd = () => {
    draggedComponentRef.current = null;
    setLocalDragState(null);
    onDragEnd();
  };

  const handleColumnDrop = (
    e: React.DragEvent,
    targetColumn: "left" | "right",
    targetIndex: number,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggedItem) {
      return;
    }

    // Handle page-item (standalone component) being dropped into layout
    if (draggedItem.type === "page-item") {
      onMoveComponentBetweenColumns(draggedItem.id, targetColumn, targetIndex);
      return;
    }

    // Handle layout-component drops
    if (draggedItem.type === "layout-component") {
      // Check if this is same-column reordering
      if (
        localDragState &&
        draggedItem.sourceLayoutId === layoutId &&
        localDragState.column === targetColumn
      ) {
        // Same column reordering - use onReorderColumn
        const sourceIndex = localDragState.index;

        // Don't reorder if dropping on itself
        if (sourceIndex === targetIndex) {
          return;
        }

        const sourceComponents =
          targetColumn === "left" ? [...leftComponents] : [...rightComponents];
        const [movedComponent] = sourceComponents.splice(sourceIndex, 1);

        // Adjust target index if moving forward (since we removed an item before the target)
        const adjustedTargetIndex = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
        sourceComponents.splice(adjustedTargetIndex, 0, movedComponent);

        const componentOrders = sourceComponents.map((component, index) => ({
          id: component.id,
          order: index,
        }));

        onReorderColumn(targetColumn, componentOrders);
      } else {
        // Cross-column or cross-layout move
        onMoveComponentBetweenColumns(draggedItem.id, targetColumn, targetIndex);
      }
    }
  };

  return (
    <Card className="p-4">
      {/* Header with drag handle - accepts drops for reordering layouts */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: drag-and-drop requires interactive div */}
      <div
        className="flex items-center justify-between mb-4"
        onDragOver={(e) => {
          // Accept page-item drags for reordering AND layout-component drags to make them standalone
          if (
            (draggedItem?.type === "page-item" && draggedItem?.id !== layoutId) ||
            draggedItem?.type === "layout-component"
          ) {
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
            e.currentTarget.classList.add("ring-2", "ring-primary", "rounded");
          }
        }}
        onDragLeave={(e) => {
          e.currentTarget.classList.remove("ring-2", "ring-primary", "rounded");
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.currentTarget.classList.remove("ring-2", "ring-primary", "rounded");

          // Handle page-item drops for reordering
          if (draggedItem?.type === "page-item" && draggedItem?.id !== layoutId) {
            onLayoutDrop?.();
          }
          // Handle layout-component drops to make them standalone (full-width)
          else if (draggedItem?.type === "layout-component") {
            onLayoutDrop?.();
          }
        }}
      >
        <div className="flex items-center gap-2">
          {/* biome-ignore lint/a11y/noStaticElementInteractions: drag handle requires drag events */}
          <div
            className="cursor-move p-1 hover:bg-accent rounded"
            draggable={!disabled}
            onDragStart={(e) => {
              // Get the actual width of the layout block - traverse up to find the parent container
              // that has the same width as standalone components (the Card's parent)
              let layoutElement = (e.currentTarget as HTMLElement).closest(
                "[data-layout-block-container]",
              ) as HTMLElement;

              // Fallback: look for the outermost Card element by going up several levels
              if (!layoutElement) {
                layoutElement = (e.currentTarget as HTMLElement).parentElement?.parentElement
                  ?.parentElement as HTMLElement;
              }

              const elementWidth = layoutElement ? layoutElement.offsetWidth : 800;

              // Create custom drag preview - full-width 2-column representation
              const dragPreview = document.createElement("div");
              dragPreview.style.cssText = `
                position: fixed;
                top: -9999px;
                left: -9999px;
                width: ${elementWidth}px;
                height: 120px;
                background: rgba(59, 130, 246, 0.2);
                border: 2px dashed rgb(59, 130, 246);
                border-radius: 8px;
                padding: 16px;
              `;
              dragPreview.innerHTML = `
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; height: 100%;">
                  <div style="background: rgba(59, 130, 246, 0.3); border-radius: 4px; border: 1px dashed rgb(59, 130, 246);"></div>
                  <div style="background: rgba(59, 130, 246, 0.3); border-radius: 4px; border: 1px dashed rgb(59, 130, 246);"></div>
                </div>
              `;
              document.body.appendChild(dragPreview);
              e.dataTransfer.setDragImage(dragPreview, 20, 60);

              // Clean up after next frame
              requestAnimationFrame(() => {
                document.body.removeChild(dragPreview);
              });

              // This is the layout drag handle - trigger parent drag logic
              e.stopPropagation();
              onLayoutDragStart?.();
            }}
            onDragEnd={(e) => {
              // Clear drag state when layout drag ends
              e.stopPropagation();
              onDragEnd();
            }}
          >
            <GripVertical className="h-5 w-5 text-muted-foreground" />
          </div>
          <Badge variant="secondary">{t.twoColumnBlock}</Badge>
          {hasLayoutConditionalDisplay() && (
            <Badge variant="secondary" className="text-xs">
              {t.conditional}
            </Badge>
          )}
        </div>
        <div className="flex gap-1">
          <Button
            size="icon"
            tooltip={t.settings}
            variant="ghost"
            onClick={onLayoutSettings}
            disabled={disabled}
          >
            <Settings className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            tooltip={t.delete}
            variant="ghost"
            onClick={onDelete}
            disabled={disabled}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Two columns - SIMPLIFIED */}
      <div className="grid grid-cols-2 gap-4">
        {/* Left column */}
        <div className="space-y-2 min-h-[100px] p-3 border-2 border-dashed rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1">
              <span className="text-xs font-medium text-muted-foreground">{t.leftColumn}</span>
              {hasColumnSettings("left") && (
                <Badge variant="secondary" className="text-[10px] px-1 py-0">
                  {t.styled}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1">
              <Button
                size="icon"
                tooltip={t.settings}
                variant="ghost"
                onClick={() => onColumnSettings("left")}
                disabled={disabled}
                className="h-6 w-6 p-0"
              >
                <Settings className="h-3 w-3" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={onAddToLeft}
                disabled={disabled}
                className="h-6 text-xs gap-1"
              >
                {t.addComponent}
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            {leftComponents.length === 0 ? (
              // biome-ignore lint/a11y/noStaticElementInteractions: drop zone requires drag events
              <div
                onDragOver={(e) => {
                  // Don't allow layout blocks to be dropped here
                  const isLayoutBlock =
                    draggedItem?.type === "page-item" && draggedItem?.itemType === "layout";
                  if (isLayoutBlock) {
                    return;
                  }
                  e.preventDefault();
                  if (draggedItem) {
                    e.currentTarget.classList.add("border-primary", "bg-primary/5");
                  }
                }}
                onDragLeave={(e) => {
                  e.currentTarget.classList.remove("border-primary", "bg-primary/5");
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove("border-primary", "bg-primary/5");
                  // Don't allow layout blocks to be dropped here
                  const isLayoutBlock =
                    draggedItem?.type === "page-item" && draggedItem?.itemType === "layout";
                  if (isLayoutBlock) {
                    return;
                  }
                  if (draggedItem) {
                    handleColumnDrop(e, "left", 0);
                  }
                }}
                className="text-center py-8 text-xs text-muted-foreground border-2 border-dashed rounded-lg transition-all"
              >
                {draggedItem &&
                !(draggedItem.type === "page-item" && draggedItem.itemType === "layout")
                  ? t.dropComponentHere
                  : t.noComponents}
              </div>
            ) : (
              leftComponents.map((component, index) => (
                <ComponentItem
                  key={component.id}
                  component={component}
                  column="left"
                  index={index}
                  draggedComponentRef={draggedComponentRef}
                  draggedItem={draggedItem}
                  disabled={disabled}
                  flowLanguage={flowLanguage}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  onDrop={handleColumnDrop}
                  onComponentSettings={onComponentSettings}
                  onComponentDelete={onComponentDelete}
                  onComponentDuplicate={onComponentDuplicate}
                  getComponentLabel={getComponentLabel}
                  hasConditionalDisplay={hasConditionalDisplay}
                  translations={t}
                />
              ))
            )}
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-2 min-h-[100px] p-3 border-2 border-dashed rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1">
              <span className="text-xs font-medium text-muted-foreground">{t.rightColumn}</span>
              {hasColumnSettings("right") && (
                <Badge variant="secondary" className="text-[10px] px-1 py-0">
                  {t.styled}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1">
              <Button
                size="icon"
                tooltip={t.settings}
                variant="ghost"
                onClick={() => onColumnSettings("right")}
                disabled={disabled}
                className="h-6 w-6 p-0"
              >
                <Settings className="h-3 w-3" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={onAddToRight}
                disabled={disabled}
                className="h-6 text-xs gap-1"
              >
                {t.addComponent}
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            {rightComponents.length === 0 ? (
              // biome-ignore lint/a11y/noStaticElementInteractions: drop zone requires drag events
              <div
                onDragOver={(e) => {
                  // Don't allow layout blocks to be dropped here
                  const isLayoutBlock =
                    draggedItem?.type === "page-item" && draggedItem?.itemType === "layout";
                  if (isLayoutBlock) {
                    return;
                  }
                  e.preventDefault();
                  if (draggedItem) {
                    e.currentTarget.classList.add("border-primary", "bg-primary/5");
                  }
                }}
                onDragLeave={(e) => {
                  e.currentTarget.classList.remove("border-primary", "bg-primary/5");
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove("border-primary", "bg-primary/5");
                  // Don't allow layout blocks to be dropped here
                  const isLayoutBlock =
                    draggedItem?.type === "page-item" && draggedItem?.itemType === "layout";
                  if (isLayoutBlock) {
                    return;
                  }
                  if (draggedItem) {
                    handleColumnDrop(e, "right", 0);
                  }
                }}
                className="text-center py-8 text-xs text-muted-foreground border-2 border-dashed rounded-lg transition-all"
              >
                {draggedItem &&
                !(draggedItem.type === "page-item" && draggedItem.itemType === "layout")
                  ? t.dropComponentHere
                  : t.noComponents}
              </div>
            ) : (
              rightComponents.map((component, index) => (
                <ComponentItem
                  key={component.id}
                  component={component}
                  column="right"
                  index={index}
                  draggedComponentRef={draggedComponentRef}
                  draggedItem={draggedItem}
                  disabled={disabled}
                  flowLanguage={flowLanguage}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  onDrop={handleColumnDrop}
                  onComponentSettings={onComponentSettings}
                  onComponentDelete={onComponentDelete}
                  onComponentDuplicate={onComponentDuplicate}
                  getComponentLabel={getComponentLabel}
                  hasConditionalDisplay={hasConditionalDisplay}
                  translations={t}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
